import type { ComponentType, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { Icon } from '@elfsight/lib-icon';
import { Show } from '@elfsight/lib-utils';
import { Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';

const COMPONENT_NAME = 'text-button';

export interface TextButtonProps {
  color: string;
  leftIcon?: ComponentType;
  rightIcon?: ComponentType;
  onClick?: () => void;
  className?: string;
}

export function TextButton({
  color,
  children,
  leftIcon,
  rightIcon,
  onClick,
  className
}: PropsWithChildren<TextButtonProps>) {
  return (
    <StyledButton onClick={onClick} className={className}>
      <Show when={!!leftIcon}>
        <Icon
          fill={color}
          size={16}
          component={leftIcon}
          className={createReadableClass({
            component: COMPONENT_NAME,
            element: 'icon',
            modifier: 'left'
          })}
        />
      </Show>

      <Text color={color} textToken="text16">
        {children}
      </Text>

      <Show when={!!rightIcon}>
        <Icon
          fill={color}
          size={16}
          component={rightIcon}
          className={createReadableClass({
            component: COMPONENT_NAME,
            element: 'icon',
            modifier: 'right'
          })}
        />
      </Show>
    </StyledButton>
  );
}

const StyledButton = styled.button.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME
  })
})`
  display: flex;
  align-items: center;
  gap: 4px;

  padding: 0;
  border: none;
  background: transparent;

  font-size: 16px;
  font-weight: 400;
  line-height: 22px;

  cursor: pointer;
  outline: none;

  min-width: 0;
  max-width: max-content;
  flex: 1 1 100%;
`;

const Text = styled(Typography).attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'text'
  })
})`
  text-decoration: none;

  ${StyledButton}:hover & {
    text-decoration: underline;
  }
`;
